/**
 * Shooting Stars
 * Randomly spawns brief meteor streaks across the night sky.
 */

import * as THREE from 'three';

const INTERVAL_MIN = 6;   // seconds between spawns (min)
const INTERVAL_MAX = 20;  // seconds between spawns (max)
const MAX_ACTIVE = 2;     // cap simultaneous stars

// Spawn geometry
const SPAWN = {
  phiMin:          Math.PI * 0.22,  // Mid-sky band — avoid zenith & horizon
  phiMax:          Math.PI * 0.46,
  radiusMin:       1500,
  radiusMax:       1900,
  segmentsMin:     10,
  segmentsMax:     20,
  trailLengthMin:  60,
  trailLengthMax:  240,
  durationMin:     0.6,
  durationMax:     1.6,
  speedFactor:     2.8,   // speed = trailLength * speedFactor / duration
  downwardMin:     0.1,   // Downward velocity bias range
  downwardMax:     0.3,
  yOffset:        -320,   // Match starfield dome y-offset
};

// Fade envelope (as fraction of total duration)
const FADE = {
  inEnd:    0.15,  // Fade-in completes at 15%
  outStart: 0.65,  // Fade-out begins at 65%
};

const NIGHT_THRESHOLD = 0.25; // sunHeightNormalized below this counts as night

let nextSpawnIn = 8;      // time until first star
const active = [];        // { line, velocity, speed, duration, elapsed, opacity }

// Shared material — all stars use the same compiled shader program.
// Per-star opacity is stored on the state object and injected via onBeforeRender.
const _sharedStarMaterial = new THREE.LineBasicMaterial({
  vertexColors: true,
  transparent:  true,
  opacity:      0.0,
  blending:     THREE.AdditiveBlending,
  depthWrite:   false,
  depthTest:    true, // occlude behind closer scene geometry (e.g. courtyard) like the starfield does
  toneMapped:   false,
  fog:          false, // streaks live on the far star dome, beyond the garden's FogExp2
});

function rnd(min, max) { return min + Math.random() * (max - min); }

function spawnStar(scene) {
  const phi    = rnd(SPAWN.phiMin, SPAWN.phiMax);
  const theta  = rnd(0, Math.PI * 2);
  const radius = rnd(SPAWN.radiusMin, SPAWN.radiusMax);

  const startPos = new THREE.Vector3();
  startPos.setFromSphericalCoords(radius, phi, theta);
  startPos.y += SPAWN.yOffset; // match starfield dome y-offset

  // Travel direction: tangent to sphere, slight downward drift
  const up    = startPos.clone().normalize();
  let   right = new THREE.Vector3().crossVectors(up, new THREE.Vector3(0, 1, 0)).normalize();
  if (right.length() < 0.001) right.set(1, 0, 0); // degenerate pole guard

  const fwd = new THREE.Vector3().crossVectors(right, up).normalize();
  const angle = rnd(0, Math.PI * 2);
  const velocity = new THREE.Vector3()
    .addScaledVector(right, Math.cos(angle))
    .addScaledVector(fwd,   Math.sin(angle));
  velocity.y -= rnd(SPAWN.downwardMin, SPAWN.downwardMax); // slight downward pull
  velocity.normalize();

  // Trail geometry — head at index 0, tail extends opposite to velocity
  const segments    = Math.floor(rnd(SPAWN.segmentsMin, SPAWN.segmentsMax));
  const trailLength = rnd(SPAWN.trailLengthMin, SPAWN.trailLengthMax);
  const duration    = rnd(SPAWN.durationMin, SPAWN.durationMax);
  const speed       = (trailLength * SPAWN.speedFactor) / duration;

  const positions = new Float32Array((segments + 1) * 3);
  const colors    = new Float32Array((segments + 1) * 3);

  const tailDir = velocity.clone().negate();
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const p = tailDir.clone().multiplyScalar(t * trailLength);
    positions[i * 3]     = p.x;
    positions[i * 3 + 1] = p.y;
    positions[i * 3 + 2] = p.z;

    // Fade head (bright) → tail (black = invisible with additive blending)
    const b = Math.pow(1.0 - t, 1.5);
    colors[i * 3]     = b;
    colors[i * 3 + 1] = b;
    colors[i * 3 + 2] = b * 0.90; // very slight blue-white tint
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color',    new THREE.Float32BufferAttribute(colors,    3));

  const starState = { line: null, velocity, speed, duration, elapsed: 0, opacity: 0 };

  const line = new THREE.Line(geometry, _sharedStarMaterial);
  line.position.copy(startPos);
  // Write this star's opacity into the shared material right before its draw call
  line.onBeforeRender = () => {
    _sharedStarMaterial.opacity = starState.opacity;
  };
  scene.add(line);

  starState.line = line;
  active.push(starState);
}

/**
 * Call once per frame from scene.js animate loop.
 * @param {THREE.Scene} scene
 * @param {number}      dt                 - delta time in seconds
 * @param {number}      sunHeightNormalized - 0 = night, 1 = full day
 */
export function updateShootingStars(scene, dt, sunHeightNormalized) {
  const isNight = sunHeightNormalized < NIGHT_THRESHOLD;

  // Countdown to next spawn
  nextSpawnIn -= dt;
  if (nextSpawnIn <= 0) {
    if (isNight && active.length < MAX_ACTIVE) {
      spawnStar(scene);
    }
    nextSpawnIn = rnd(INTERVAL_MIN, INTERVAL_MAX);
  }

  // Animate active stars
  for (let i = active.length - 1; i >= 0; i--) {
    const s = active[i];
    s.elapsed += dt;
    const t = s.elapsed / s.duration;

    if (t >= 1.0) {
      scene.remove(s.line);
      s.line.geometry.dispose();
      // Shared material — not disposed per star
      active.splice(i, 1);
      continue;
    }

    // Move the head forward
    s.line.position.addScaledVector(s.velocity, s.speed * dt);

    if (t < FADE.inEnd) {
      s.opacity = t / FADE.inEnd;
    } else if (t > FADE.outStart) {
      s.opacity = 1.0 - (t - FADE.outStart) / (1.0 - FADE.outStart);
    } else {
      s.opacity = 1.0;
    }
  }
}
